import React, { useState } from 'react';
import { Flex, Typography, Button } from 'antd';
import {
  LockOutlined,
  CalendarOutlined,
  ExportOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  formatSubscriptionEndDate,
  SUBSCRIPTION_POST_EXPIRY_RETENTION_DAYS,
  CONTACT_GROUP_ADMIN_MESSAGE,
  getSelfOnboardingPortalUrl,
  shouldShowSelfOnboardingPortalLink,
} from '../utils/subscriptionPlan';
import {
  openManageProfileLink,
  usesManageProfileLinkForSelfOnboarding,
} from '../utils/openManageProfileLink';

const buildBannerStyle = (isDarkMode) => ({
  marginBottom: 16,
  borderRadius: 14,
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${isDarkMode ? 'rgba(250, 140, 22, 0.32)' : 'rgba(250, 140, 22, 0.22)'}`,
  background: isDarkMode
    ? 'linear-gradient(125deg, rgba(250, 84, 28, 0.16) 0%, rgba(38, 28, 18, 0.95) 42%, #1a1a1a 100%)'
    : 'linear-gradient(125deg, #fff1e6 0%, #fff9f0 35%, #ffffff 100%)',
  boxShadow: isDarkMode
    ? '0 6px 24px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.04)'
    : '0 6px 22px rgba(250, 140, 22, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
});

const glowOrbStyle = (isDarkMode) => ({
  position: 'absolute',
  top: -28,
  right: 48,
  width: 88,
  height: 88,
  borderRadius: '50%',
  background: isDarkMode
    ? 'radial-gradient(circle, rgba(250, 140, 22, 0.22) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(255, 169, 64, 0.35) 0%, transparent 70%)',
  pointerEvents: 'none',
});

const accentStripeStyle = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 3,
  background: 'linear-gradient(180deg, #ff7a45 0%, #fa8c16 50%, #ffc069 100%)',
};

const iconWrapStyle = {
  width: 44,
  height: 44,
  borderRadius: 12,
  background: 'linear-gradient(145deg, #ff7a45 0%, #fa541c 48%, #fa8c16 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 20,
  flexShrink: 0,
  boxShadow: '0 4px 14px rgba(250, 84, 28, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
};

export default function SubscriptionExpiredBanner({
  endDate,
  isDarkMode = false,
  unrestrictedAccess = false,
  roleName,
  subscriptionPlanDetails = null,
}) {
  const authToken = useSelector((state) => state.auth.token);
  const [portalLoading, setPortalLoading] = useState(false);
  const formattedEnd = endDate ? formatSubscriptionEndDate(endDate) : null;
  const portalUrl = getSelfOnboardingPortalUrl();
  const useManageProfileLink = usesManageProfileLinkForSelfOnboarding(roleName, authToken);
  const showPortalLink = shouldShowSelfOnboardingPortalLink(
    roleName,
    subscriptionPlanDetails,
    authToken
  );

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      await openManageProfileLink(authToken);
    } finally {
      setPortalLoading(false);
    }
  };

  const canShowPortalButton =
    showPortalLink && (useManageProfileLink || portalUrl);

  const titleColor = isDarkMode ? '#ffe7ba' : '#873800';
  const bodyColor = isDarkMode ? 'rgba(255, 236, 214, 0.78)' : 'rgba(60, 40, 20, 0.72)';
  const accentColor = isDarkMode ? '#ffc069' : '#d4380d';

  const statusPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.02em',
    background: unrestrictedAccess
      ? isDarkMode ? 'rgba(22, 119, 255, 0.2)' : 'rgba(22, 119, 255, 0.1)'
      : isDarkMode ? 'rgba(250, 140, 22, 0.22)' : 'rgba(250, 140, 22, 0.14)',
    color: unrestrictedAccess
      ? isDarkMode ? '#91caff' : '#0958d9'
      : isDarkMode ? '#ffd591' : '#ad4e00',
    border: `1px solid ${unrestrictedAccess
      ? isDarkMode ? 'rgba(22, 119, 255, 0.35)' : 'rgba(22, 119, 255, 0.2)'
      : isDarkMode ? 'rgba(250, 173, 20, 0.35)' : 'rgba(250, 140, 22, 0.25)'}`,
  };

  return (
    <div role="alert" style={buildBannerStyle(isDarkMode)}>
      <div style={accentStripeStyle} aria-hidden />
      <div style={glowOrbStyle(isDarkMode)} aria-hidden />

      <Flex align="center" gap={14} wrap="wrap" style={{ padding: '14px 16px 14px 18px' }}>
        <div style={iconWrapStyle}>
          <LockOutlined />
        </div>

        <Flex vertical style={{ flex: 1, minWidth: 220 }} gap={6}>
          <Flex align="center" gap={10} wrap="wrap">
            <Typography.Text
              strong
              style={{
                fontSize: 15,
                fontWeight: 650,
                letterSpacing: '-0.025em',
                color: titleColor,
              }}
            >
              Subscription ended
            </Typography.Text>
            <span style={statusPillStyle}>
              {unrestrictedAccess ? 'Full access' : 'Read-only'}
            </span>
          </Flex>

          <Typography.Text style={{ fontSize: 13, lineHeight: 1.55, color: bodyColor }}>
            {unrestrictedAccess
              ? 'Other users can view data only; you retain full admin access.'
              : 'Create, edit, submit, and share are disabled — view-only mode is active.'}
          </Typography.Text>

          {showPortalLink ? (
            <Flex align="center" gap={6} wrap="wrap" style={{ fontSize: 12, color: bodyColor }}>
              <FieldTimeOutlined style={{ color: '#fa8c16', fontSize: 13 }} />
              <span>
                Retained for{' '}
                <Typography.Text strong style={{ color: accentColor }}>
                  {SUBSCRIPTION_POST_EXPIRY_RETENTION_DAYS} days
                </Typography.Text>
                {' '}— renew before permanent deletion.
              </span>
            </Flex>
          ) : (
            <Typography.Text style={{ fontSize: 13, lineHeight: 1.55, color: bodyColor }}>
              {CONTACT_GROUP_ADMIN_MESSAGE}
            </Typography.Text>
          )}

          {canShowPortalButton ? (
            <Button
              size="small"
              type="default"
              icon={<ExportOutlined />}
              href={useManageProfileLink ? undefined : portalUrl}
              target={useManageProfileLink ? undefined : '_blank'}
              rel={useManageProfileLink ? undefined : 'noopener noreferrer'}
              onClick={useManageProfileLink ? handleOpenPortal : undefined}
              loading={portalLoading}
              style={{
                alignSelf: 'flex-start',
                marginTop: 2,
                borderRadius: 8,
                fontWeight: 500,
                fontSize: 12,
                height: 28,
                borderColor: isDarkMode ? 'rgba(250, 140, 22, 0.45)' : '#ffc591',
                color: isDarkMode ? '#ffd591' : '#d46b08',
                background: isDarkMode ? 'rgba(250, 140, 22, 0.12)' : '#fff7e6',
              }}
            >
              Renew subscription
            </Button>
          ) : null}
        </Flex>

        {formattedEnd && (
          <Flex
            align="center"
            gap={8}
            style={{
              padding: '9px 13px',
              borderRadius: 10,
              background: isDarkMode ? 'rgba(0, 0, 0, 0.28)' : 'rgba(255, 255, 255, 0.85)',
              border: `1px solid ${isDarkMode ? 'rgba(250, 173, 20, 0.22)' : 'rgba(250, 140, 22, 0.16)'}`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: isDarkMode ? 'rgba(250, 140, 22, 0.15)' : 'rgba(250, 140, 22, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarOutlined style={{ color: '#fa8c16', fontSize: 15 }} />
            </div>
            <Flex vertical gap={0}>
              <Typography.Text
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  color: isDarkMode ? 'rgba(255, 236, 214, 0.5)' : 'rgba(0, 0, 0, 0.42)',
                }}
              >
                Expired
              </Typography.Text>
              <Typography.Text
                strong
                style={{
                  fontSize: 13,
                  letterSpacing: '-0.01em',
                  color: isDarkMode ? '#ffd591' : '#d4380d',
                }}
              >
                {formattedEnd}
              </Typography.Text>
            </Flex>
          </Flex>
        )}
      </Flex>
    </div>
  );
}
